<template>
  <b-container fluid="md">
    <h1>Client</h1>
    <div class="container">
      <form>
        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            class="form-control"
            id="name"
            v-model="client.name"
            @input="updateField"
          />
        </div>
        <div class="form-group">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              :checked="client.authorized"
              v-model="client.authorized"
              id="authorized"
              @input="updateField"
            />
            <label class="form-check-label" for="authorized">Authorized</label>
          </div>
        </div>
        <div class="form-group">
          <label for="nbRooms">Room count</label>
          <input
            type="number"
            class="form-control"
            id="nbRooms"
            v-model="client.nbRooms"
            @input="updateField"
          />
        </div>
        <div class="form-group">
          <label for="siren">SIREN</label>
          <input
            type="text"
            required
            class="form-control"
            id="siren"
            v-model="client.siren"
            @input="updateField"
          />
          <small v-if="tvaNumberGenerated" class="text-muted"
            >Numéro TVA: {{ tvaNumberGenerated }}</small
          >
        </div>
        <div class="form-group">
          <label for="siren">Numéro TVA</label>
          <input
            type="text"
            class="form-control"
            id="siren"
            v-model="client.tvaNumber"
            @input="updateField"
          />
        </div>
        <div class="form-group">
          <label>Langues de contenu</label>
          <div class="mb-2">
            <b-badge
              v-for="code in client.dataLanguages"
              :key="code"
              variant="info"
              class="mr-1 mb-1"
            >
              {{ getLanguageLabel(code) }}
              <button
                type="button"
                class="close ml-1"
                aria-label="Supprimer"
                @click="removeLanguage(code)"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </b-badge>
          </div>
          <vue-typeahead-bootstrap
            ref="languageTypeahead"
            v-model="languageSearch"
            :data="filteredLanguages"
            :serializer="lang => lang.label"
            :minMatchingChars="0"
            :showOnFocus="true"
            :maxMatches="1000"
            placeholder="Rechercher une langue..."
            @hit="addLanguage($event)"
          />
        </div>
      </form>
    </div>

    <div class="container">
      <button v-on:click="save" :disabled="!updated" class="btn btn-primary">
        Sauvegarder
      </button>
    </div>

    <hr class="mt-4" />

    <template v-if="!isCreating">
      <h2 class="mt-4">Salles</h2>
      <b-table
        outlined
        :items="roomList"
        :fields="roomFields"
        sort-by="_id"
        primary-key="_id"
        head-variant="light"
      >
        <template #cell(_id)="data">
          <router-link :to="{ name: 'Room', params: { roomId: data.value } }">{{
            data.value
          }}</router-link>
        </template>

        <template #cell(authorized)="data">
          <b-badge v-if="data.value == true" variant="success">Oui</b-badge>
          <b-badge v-else variant="danger">Non</b-badge>
        </template>

        <template #cell(lastConnectionDate)="data">
          {{ data.value ? dateFormat(data.value) : '' }}
        </template>

        <template #cell(serverVersion)="data">
          <span :id="'version_tooltip__' + data.item._id">{{
            data.item.metadata
              ? data.item.metadata.serverVersion ||
                data.item.metadata.version_package
              : 'Inconnue'
          }}</span>

          <b-tooltip
            v-if="data.item.metadata && data.item.metadata.version_package"
            :target="'version_tooltip__' + data.item._id"
            placement="top"
            class="text-left"
          >
            <div class="text-left m-0">
              <div class="d-flex justify-content-between">
                <div class="mr-2">Application:</div>
                <div>
                  {{ data.item.metadata.version_package || 'Inconnue' }}
                </div>
              </div>
              <div class="d-flex justify-content-between">
                <div class="mr-2">Contenu:</div>
                <div>{{ formatDataVersion(data.item.metadata) }}</div>
              </div>
            </div>
          </b-tooltip>
        </template>

        <template #cell(dataVersion)="data">
          <span :id="'package_tooltip__' + data.item._id">{{
            formatDataVersion(data.item.metadata)
          }}</span>
        </template>

        <template #cell(betaAccess)="data">
          <b-badge v-if="data.value == true" variant="warning">Beta</b-badge>
          <b-badge v-else variant="light">Non</b-badge>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex">
            <router-link
              tag="button"
              class="btn btn-sm btn-info mr-1"
              :to="{ name: 'Logs', params: { roomId: data.item._id } }"
              >Logs</router-link
            >
            <b-button
              v-if="data.item.betaAccess == true"
              size="sm"
              variant="warning"
              @click="updateBetaAccess(data.item, false)"
              >Désactiver Beta mode</b-button
            >
            <b-button
              v-else
              type="button"
              size="sm"
              variant="success"
              @click="updateBetaAccess(data.item, true)"
              >Activer Beta mode</b-button
            >
            <b-button
              size="sm"
              variant="warning"
              class="ml-1"
              @click="deleteRoom(data.item)"
              >Dissocier</b-button
            >
          </div>
        </template>
      </b-table>

      <h2>Ajouter une salle</h2>
      <b-form @submit.prevent="addRoom" class="mb-4">
        <b-form-group label="Saisir l'ID d'une salle:" label-for="roomId">
          <b-form-input
            type="text"
            id="roomId"
            v-model="roomId"
            placeholder="Salle ID"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group
          label="Ou choisissez une salle non affectée:"
          label-for="roomIdSelect"
        >
          <b-form-select id="roomIdSelect" v-model="roomId">
            <option value="">Choisir</option>
            <option
              v-for="room in notAffectedRooms"
              :key="room._id"
              :value="room._id"
            >
              {{ room._id }}
            </option>
          </b-form-select>
        </b-form-group>

        <b-button type="submit" variant="primary">Ajouter la salle</b-button>
      </b-form>
    </template>
  </b-container>
</template>

<script>
  import axios from 'axios'
  import moment from 'moment'
  import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap'
  import { formatDataVersion } from '@/support/metadata.support'

  export default {
    name: 'Client',
    computed: {
      isCreating() {
        return this.clientId === 'new'
      },
      roomFields() {
        return [
          {
            key: '_id',
            label: 'ID',
            sortable: true,
            sortDirection: 'asc',
            tdClass: 'text-center font-weight-bold',
          },
          {
            key: 'authorized',
            label: 'Salle autorisée',
            sortable: true,
            sortDirection: 'asc',
          },
          {
            key: 'lastConnectionDate',
            label: 'Dernière connexion le',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'serverVersion',
            label: 'Numéro de version',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'dataVersion',
            label: 'Version de contenu',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'ip',
            label: 'Adresse IP',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'betaAccess',
            label: 'Beta testeur',
            sortable: true,
            tdClass: 'text-center',
          },
          {
            key: 'actions',
            label: '',
            tdClass: 'text-center',
          },
        ]
      },
      roomList() {
        return this.rooms
      },
      filteredLanguages() {
        return this.availableLanguages.filter(
          (lang) => !this.client.dataLanguages.includes(lang.code)
        )
      },
      clientComputed() {
        return JSON.parse(JSON.stringify(this.client))
      },
    },
    data() {
      return {
        refreshIncrement: 1,
        clientId: this.$route.params.clientId || 'new',
        client: {
          authorized: true,
          nbRooms: 1,
          siren: null,
          tvaNumber: null,
          dataLanguages: [],
        },
        rooms: [],
        notAffectedRooms: [],
        roomId: '',
        tvaNumberGenerated: null,
        updated: false,
        availableLanguages: [],
        languageSearch: '',
      }
    },
    watch: {
      clientComputed: {
        handler: function (val, oldVal) {
          if (
            oldVal.siren !== val.siren &&
            val.siren.toString().length === 9 &&
            val.siren.toString().match(/^[0-9]+$/)
          ) {
            // Guess if its NOT a french company via regex. If the name starts with something between brackets, we check that the content is other than FR
            const isFrenchCompany =
              !val.name.match(/^\([A-Z]{2}\)/) ||
              val.name
                .match(/^\([A-Z]{2}\)/)[0]
                .replace('(', '')
                .replace(')', '') === 'FR'
            if (isFrenchCompany) {
              this.tvaNumberGenerated = this.calculateTVANumber(val.siren)
            }
          }
        },
        deep: true,
      },
    },
    components: { VueTypeaheadBootstrap },
    mounted() {
      this.getClient()
      this.fetchNotAffectedRooms()
      this.fetchLanguages()
    },
    methods: {
      fetchLanguages() {
        const token = localStorage.getItem('jwt')
        if (token) {
          axios
            .get('/api/language', {
              withCredentials: true,
              headers: {
                Authorization: 'Bearer ' + token,
              },
            })
            .then((response) => {
              this.availableLanguages = response.data
            })
            .catch((err) => {
              console.error('Failed to fetch languages:', err.message)
            })
        }
      },
      addLanguage(lang) {
        if (!this.client.dataLanguages) {
          this.client.dataLanguages = []
        }
        if (!this.client.dataLanguages.includes(lang.code)) {
          this.client.dataLanguages.push(lang.code)
          this.updateField()
        }
        setTimeout(() => {
          this.languageSearch = ''
          this.$refs.languageTypeahead.$refs.input.blur()
          this.$refs.languageTypeahead.$refs.input.focus()
        }, 100)
      },
      removeLanguage(code) {
        this.client.dataLanguages = this.client.dataLanguages.filter(
          (c) => c !== code
        )
        this.updateField()
      },
      getLanguageLabel(code) {
        const lang = this.availableLanguages.find((l) => l.code === code)
        return lang ? lang.label : code
      },
      addRoom() {
        if (this.notAffectedRooms.length > 0) {
          let index = this.notAffectedRooms.findIndex(
            (room) => room._id == this.roomId
          )

          if (index > -1) {
            let room = this.notAffectedRooms[index]
            room.authorized = true
            this.rooms.push(room)
            this.notAffectedRooms.splice(index, 1)
          }
        }
        this.roomId = ''

        this.save()
      },
      deleteRoom(item) {
        let confirm = window.confirm(
          'Êtes-vous sur de vouloir dissocier la salle "' + item._id + '" ?'
        )
        if (confirm === false) {
          return
        }

        const token = localStorage.getItem('jwt')
        const headers = {
          withCredentials: true,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
        if (token) {
          axios
            .post(
              '/api/client/' + this.client._id + '/deleteRoom',
              { roomId: item._id },
              headers
            )
            .then(() => {
              let index = this.rooms.indexOf(item)
              console.log('Room ' + item._id + ' deleted')
              this.rooms.splice(index, 1)
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else {
          this.$router.push('/login')
        }
      },
      updateField() {
        console.log('updateField')
        this.updated = true
      },
      save() {
        if (this.isCreating) {
          this.create()
        } else {
          this.update()
        }
      },
      create() {
        const token = localStorage.getItem('jwt')
        const headers = {
          withCredentials: true,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
        const data = this.client
        data.rooms = this.rooms
        data.nbActiveRooms = 0
        data.siren = data.siren ? parseInt(data.siren) : null
        console.log('create', data)

        if (token) {
          axios
            .post('/api/client', data, headers)
            .then(() => {
              this.updated = false

              this.$notify({
                type: 'success',
                title: 'Salle créée',
              })

              this.$router.push('/client')
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else {
          this.$router.push('/login')
        }
      },
      update() {
        const token = localStorage.getItem('jwt')
        const headers = {
          withCredentials: true,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
        const data = this.client
        data.rooms = this.rooms
        data.siren = data.siren ? data.siren : null

        console.log('save', data)

        if (token) {
          axios
            .post('/api/client', data, headers)
            .then(() => {
              this.updated = false

              this.$notify({
                type: 'success',
                title: 'Salle mise à jour',
              })

              this.getClient()
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else {
          this.$router.push('/login')
        }
      },
      async updateBetaAccess(room, betaAccess) {
        const token = localStorage.getItem('jwt')
        if (token) {
          let response = await axios.post('/api/room/' + room._id, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
            betaAccess: betaAccess,
          })

          if (response.status === 200) {
            let index = this.rooms.findIndex((item) => item._id === room._id)
            this.rooms[index].betaAccess = betaAccess

            this.$notify({
              type: betaAccess ? 'success' : 'warn',
              title: betaAccess
                ? 'Activation du mode beta'
                : 'Désactivation du mode beta',
            })

            this.refreshIncrement++
            this.$forceUpdate()
          } else {
            this.$notify({
              type: 'error',
              title: 'Erreur',
              text: response.data.message || 'Une erreur est survenue',
            })
          }
        } else {
          this.$router.push('/login')
        }
      },
      fetchNotAffectedRooms() {
        const token = localStorage.getItem('jwt')

        if (token) {
          axios
            .get('/api/room', {
              withCredentials: true,
              headers: {
                Authorization: 'Bearer ' + token,
              },
            })
            .then((response) => {
              this.notAffectedRooms = response.data.filter(
                (room) => !room.client
              )
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else {
          this.$router.push('/login')
        }
      },
      getClient() {
        const token = localStorage.getItem('jwt')
        if (token && this.clientId && this.clientId !== 'new') {
          axios
            .get('/api/client/' + this.clientId, {
              withCredentials: true,
              headers: {
                Authorization: 'Bearer ' + token,
              },
            })
            .then((response) => {
              this.client = response.data
              this.$set(this.client, 'dataLanguages', this.client.dataLanguages || [])
              this.rooms = this.client.rooms
            })
            .catch(() => {
              this.$router.push('/login')
            })
        } else if (!token) {
          this.$router.push('/login')
        }
      },
      formatDataVersion,
      dateFormat: function (date) {
        return moment(date).local().format('YYYY-MM-DD HH:mm:ss')
      },
      calculateTVANumber(siren) {
        let sirenNumber = parseInt(siren)
        if (sirenNumber !== NaN) {
          let tvaNumber = (12 + 3 * (sirenNumber % 97)) % 97
          if (tvaNumber < 10) {
            return 'FR0' + tvaNumber + sirenNumber
          } else {
            return 'FR' + tvaNumber + sirenNumber
          }
        }
      },
    },
  }
</script>

<style scoped>
  .container {
    margin-top: 2%;
  }

  form {
    width: 40%;
  }

  .roomTable {
    width: 55%;
  }

  .roomActionCol {
    text-align: center;
  }

  .badge .close {
    font-size: 1rem;
    color: white;
    text-shadow: none;
    opacity: 0.8;
    line-height: 1;
  }

  .badge .close:hover {
    opacity: 1;
  }
</style>
